import { v4 as uuidv4 } from "uuid"
import { DateTime } from "luxon"
import { asyncAction } from "../common"
import { getState } from "storeAccess"
import { limsRequest } from "./limsRequest"
import * as limsSampleQc from "./limsSampleQc"
import * as samples from "../samples"

// RERUN-link records (PRD-003 Area 4 / SPEC-010). When QA signs a `may-reanalyse` decision the
// disposition immediately creates the RERUN sample: a NEW sample with the SAME human sample
// number (`sampleName`) + `productName` as the original, a NEW backend id, a freshly generated
// `uniqueSampleId`, `status: "RERUN"`, placed on a NEW analysis run — as an UNPLANNED item ready
// to plan. The ORIGINAL sample stays untouched on the completed run (the rerun is a brand-new
// row by construction).
//
// The link between the original and the rerun lives HERE (the real /lims/reruns endpoints; the
// backend dedups by originalSampleId and audits) rather than on the sample payload, because the
// /samples schema rejects unknown fields (422) — the same "real planner sample + LIMS link
// metadata" split as sampleQc. `status: "RERUN"` is attempted on the create input and the FE
// tolerates the backend ignoring/normalising it. See docs/specs/lims-api-contract.md.
//
// Record shape:
//   { id, originalSampleId, rerunSampleId, sampleNumber, productName, analysisId,
//     qaDecisionId, createdAt }
const initialState = { items: [], isLoading: false }

export function reducer(state = initialState, action) {
  switch (action.type) {
    case "limsReruns/fetch/BEGIN":
      return { ...state, isLoading: true }
    case "limsReruns/fetch":
      return { items: action.res, isLoading: false }
    case "limsReruns/create":
      return { ...state, items: [...state.items, action.res] }
    case "auth/logout":
      return initialState
    default:
      return state
  }
}

export const fetch = asyncAction("limsReruns/fetch", (_dispatch) =>
  limsRequest("GET", "/lims/reruns").then((res) => res.items)
)

// Pull a created sample id out of whatever shape samples.createSample resolves to (mirrors
// AddEditSamplePopup.extractCreatedSampleId).
function extractCreatedSampleId(res) {
  if (!res) return null
  let first
  if (Array.isArray(res.samples)) first = res.samples[0]
  else if (res.sample) first = res.sample
  else if (Array.isArray(res)) first = res[0]
  else first = res
  if (!first) return null
  return first.sampleId || first.id || first.sample_id || null
}

// Create the RERUN for an original sample, GUARDED by a prior `may-reanalyse` QA decision
// existing for the original sample's OOS. `args`:
//   { originalSample: { id, sampleName, productName, analysisIds?, deadline?, arrival? },
//     qaDecisionId, analysisId, hasApproval }
// `hasApproval` is the caller-resolved "a may-reanalyse decision exists" guard (the caller has
// the qaDecisions slice in scope); the thunk also rejects defensively if it is false. On success
// it creates a new sample (same number + product, status RERUN), clones the original's QC with a
// fresh uniqueSampleId, stores the link, and writes a `limsAuditRerunCreated` audit entry.
export const createRerun = asyncAction(
  "limsReruns/create",
  (dispatch, { originalSample, qaDecisionId, analysisId, hasApproval, deadline, arrival, viaDiscard = false, signature = null } = {}) => {
    if (!originalSample || !originalSample.id) {
      return Promise.reject(new Error("createRerun: no original sample"))
    }
    // SPEC-031: a rerun is authorised EITHER by a QA may-reanalyse decision (hasApproval) OR by a
    // signed "sample discarded (lost/destroyed)" deviation (viaDiscard). One of the two is required.
    if (!hasApproval && !viaDiscard) {
      return Promise.reject(new Error("createRerun: a may-reanalyse decision or a discard authorization is required"))
    }
    // Don't create a second rerun for the same original.
    if (selectRerunForOriginal(getState(), originalSample.id)) {
      return Promise.reject(new Error("createRerun: a rerun already exists for this sample"))
    }

    const sampleNumber = originalSample.sampleName || ""
    const productName = originalSample.productName || null
    const nowISO = DateTime.now().toISO()
    // A new observation/run: a fresh observationId so the rerun is a distinct row from the
    // original (which keeps its place on the completed run).
    const input = {
      sampleName: sampleNumber,
      productName,
      analysisIds: Array.isArray(originalSample.analysisIds) ? originalSample.analysisIds : [],
      observationId: String(Date.now()),
      numberOfObservations: 1,
      arrival: arrival || nowISO,
      deadline: deadline || DateTime.now().plus({ days: 7 }).toISO(),
      priority: 2,
      // SP-E must accept this; today the deployed schema may ignore/normalise it (the rerun
      // identity is held by this `reruns` link + limsSampleQc until then).
      status: "RERUN",
      exclusive: false,
      isFlagged: false,
    }

    return dispatch(samples.createSample(input)).then((res) => {
      const newSampleId = extractCreatedSampleId(res)
      // Clone the original's QC details onto the rerun, with a FRESH uniqueSampleId (a physically
      // distinct analysis must not share the original's unique id).
      if (newSampleId) {
        const originalQc = limsSampleQc.selectSampleQc(getState(), originalSample.id) || {}
        const clonedQc = { ...originalQc, uniqueSampleId: `RERUN-${uuidv4().slice(0, 8)}` }
        dispatch(limsSampleQc.setForSample(newSampleId, clonedQc)).catch(() => {})
      }

      const record = {
        originalSampleId: originalSample.id,
        rerunSampleId: newSampleId || null,
        sampleNumber,
        productName,
        analysisId: analysisId || null,
        qaDecisionId: qaDecisionId || null,
        // SPEC-031: a discard-authorised rerun records WHY it was raised (a lost/destroyed sample),
        // distinct from a QA may-reanalyse rerun (which carries a qaDecisionId instead).
        reason: viaDiscard ? "discard" : null,
        discardReason: viaDiscard && signature ? signature.reason || null : null,
        createdAt: nowISO,
      }
      // The backend dedups (one rerun link per original sample) and writes the
      // limsAuditRerunCreated audit entry server-side. SPEC-031: a DISCARD-authorised rerun rides the
      // analyst's e-signature password (+ reason) at the request-body top level — the same transport
      // as every other signed LIMS transition — so the server verifies + audits the deviation.
      const signFields =
        viaDiscard && signature
          ? {
              ...(signature.password ? { password: signature.password } : {}),
              ...(signature.reason ? { reason: signature.reason } : {}),
            }
          : {}
      return limsRequest("POST", "/lims/reruns", { input: record, ...signFields }).then((stored) => stored.item)
    })
  }
)

// The rerun-link record for an original sample (or undefined if none yet) — the FORWARD link
// (original → its rerun).
export const selectRerunForOriginal = (state, originalSampleId) =>
  state.limsReruns.items.find((r) => r.originalSampleId === originalSampleId)

// The rerun-link record whose RERUN sample is `rerunSampleId` (or undefined) — the REVERSE link
// (rerun → its original), so a rerun sample can show "Rerun of <original>" and the chain is
// traceable both ways (SPEC-022 F2 / K16).
export const selectRerunOriginalFor = (state, rerunSampleId) =>
  state.limsReruns.items.find((r) => r.rerunSampleId === rerunSampleId)

export const selectReruns = (state) => state.limsReruns.items
